import {request, RequestOptions} from './apiClient';

const onboardingRequest = (path: string, options: RequestOptions = {}) =>
  request(path, options);

export interface ProvisionUserArgs {
  provider?: string;
  email?: string | null;
  providerName?: string;
  specialty?: string;
  referralSource?: string;
  superAdminPreferences?: {mockEhrMode: boolean};
}

export interface OnboardingFacility {
  id: string;
  facility_name?: string;
  name?: string;
  location?: string;
  address?: string;
}

/**
 * Provision the current Firebase user on the backend.
 * Returns { user, requires_facility_selection, facilities, repaired_existing_user }.
 */
export async function provisionUser({
  provider = 'email',
  email,
  providerName,
  specialty,
  referralSource,
  superAdminPreferences,
}: ProvisionUserArgs = {}) {
  const body: Record<string, unknown> = {provider};
  if (email) body.email = email;
  if (providerName) body.providerName = providerName;
  if (specialty) body.specialty = specialty;
  if (referralSource) body.referralSource = referralSource;
  if (superAdminPreferences) {
    body.super_admin_preferences = superAdminPreferences;
  }

  return onboardingRequest('/api/v1/onboarding/user', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function syncSuperAdminPreferences(superAdminPreferences: {
  mockEhrMode: boolean;
}) {
  return onboardingRequest('/api/v1/onboarding/user', {
    method: 'POST',
    body: JSON.stringify({
      super_admin_preferences: superAdminPreferences,
    }),
  });
}

/**
 * Fetch available facilities for the current user's company.
 */
export async function fetchOnboardingFacilities() {
  return onboardingRequest('/api/v1/onboarding/facilities');
}

/**
 * Assign a facility to the current user during onboarding.
 */
export async function selectOnboardingFacility(facilityId: string) {
  return onboardingRequest('/api/v1/onboarding/facility', {
    method: 'PATCH',
    body: JSON.stringify({facilityId}),
  });
}

/**
 * Link a verified Microsoft identity to an existing Firebase user without
 * requiring the user's old password.
 */
export async function linkMicrosoftProvider(idToken: string): Promise<{
  customToken: string;
}> {
  return onboardingRequest('/api/v1/onboarding/link-microsoft', {
    method: 'POST',
    auth: false,
    body: JSON.stringify({idToken}),
  });
}
